/**
 * CycleObjectives Page Component
 *
 * Shows all objectives within a specific cycle.
 * Accessed via /okr/cycles/:cycleId
 * Displays hierarchical breadcrumb navigation.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Timestamp } from 'firebase/firestore';
import { PlusIcon } from '@heroicons/react/24/outline';
import type { CycleObjectivesProps } from './cycleObjectives.types';
import { Breadcrumb } from '../../components/Breadcrumb';
import { ObjectiveCard } from '../../components/ObjectiveCard';
import type { ResponsiblePerson } from '../../components/ObjectiveCard/objectiveCard.types';
import CreateObjectiveModal from '../Objectives/components/CreateObjectiveModal';
import { useObjectivesRealtimeQuery } from '../../hooks/data/useObjectivesRealtimeQuery';
import { useObjectiveMutations } from '../../hooks/data/useObjectiveMutations';
import { useCyclesQuery } from '../../hooks/data/useCyclesQuery';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateRangeIgnoringTimezone } from '../../utils/dateFormatting';
import { keyResultService } from '../../services/keyResultService';
import { useCollaboratorsPublicQuery } from '@/features/Collaborators/Data/hooks/useCollaboratorsPublicQuery';

export default function CycleObjectives({ className }: CycleObjectivesProps) {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [cycleKeyResults, setCycleKeyResults] = useState<any[]>([]);

  // Mutations
  const { createObjective } = useObjectiveMutations();

  // Fetch cycle data for breadcrumb and display
  const { cycles, isLoading: cyclesLoading } = useCyclesQuery();
  const cycle = cycles.find((c) => c.id === cycleId);

  // Fetch objectives for this cycle
  const { objectives, isLoading: objectivesLoading, error } = useObjectivesRealtimeQuery(cycleId);

  // Fetch collaborators for responsible photos
  const { data: collaboratorsPublic = [] } = useCollaboratorsPublicQuery();

  // Fetch key results for this cycle
  useEffect(() => {
    if (cycleId) {
      keyResultService.getByCycle(cycleId).then(setCycleKeyResults);
    }
  }, [cycleId]);

  // Group responsibles by objective
  const objectivesWithResponsibles = useMemo(() => {
    return objectives.map((objective) => {
      const objectiveKRs = cycleKeyResults.filter((kr) => kr.objectiveId === objective.id);
      const responsiblesMap = new Map<string, ResponsiblePerson>();

      objectiveKRs.forEach((kr) => {
        if (kr.responsibleId && !responsiblesMap.has(kr.responsibleId)) {
          // Find collaborator photo from public data
          const collaborator = collaboratorsPublic.find(c => c.id === kr.responsibleId);

          responsiblesMap.set(kr.responsibleId, {
            id: kr.responsibleId,
            name: kr.responsibleName,
            email: kr.responsibleEmail,
            photoUrl: collaborator?.photoUrl || undefined,
          });
        }
      });

      return {
        ...objective,
        responsibles: Array.from(responsiblesMap.values()),
      };
    });
  }, [objectives, cycleKeyResults, collaboratorsPublic]);

  // Handle objective click - navigate to details
  const handleObjectiveClick = (objectiveId: string) => {
    navigate(`/okr/cycles/${cycleId}/objectives/${objectiveId}`);
  };

  // Handle create objective
  const handleCreateObjective = async (data: any) => {
    if (!user || !cycleId) return;

    try {
      const tempPath = `/${cycleId}/temp`;

      await createObjective.mutateAsync({
        ...data,
        cycleId: cycleId,
        ownerId: user.uid,
        ownerName: user.displayName || user.email || 'Unknown',
        ownerEmail: user.email || '',
        path: tempPath,
        parentObjectiveId: null,
        parentObjectivePath: null,
        // Convert Date to Timestamp for Firestore
        startDate: data.startDate instanceof Date ? Timestamp.fromDate(data.startDate) : data.startDate,
        endDate: data.endDate instanceof Date ? Timestamp.fromDate(data.endDate) : data.endDate,
      });
      setIsCreateModalOpen(false);
    } catch (err) {
      console.error('Error creating objective:', err);
    }
  };

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: 'OKR', path: '/okr' },
    { label: cycle?.name || 'Ciclo', path: undefined }, // Current page
  ];

  // Loading state
  if ((cyclesLoading || objectivesLoading) && objectives.length === 0) {
    return (
      <div className={clsx('p-6', className)}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-600"></div>
            <p className="mt-4 font-montserrat text-gray-600">Carregando objetivos...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={clsx('p-6', className)}>
        <Breadcrumb items={breadcrumbItems} className="mb-6" />
        <div className="rounded-lg border-2 border-red-200 bg-red-50 p-6 text-center">
          <p className="font-montserrat text-lg text-red-800">
            Erro ao carregar objetivos
          </p>
          <p className="mt-2 text-sm text-red-600">
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
        </div>
      </div>
    );
  }

  // Cycle not found
  if (!cyclesLoading && !cycle) {
    return (
      <div className={clsx('p-6', className)}>
        <Breadcrumb items={breadcrumbItems} className="mb-6" />
        <div className="rounded-lg border-2 border-yellow-200 bg-yellow-50 p-6 text-center">
          <p className="font-montserrat text-lg text-yellow-800">
            Ciclo não encontrado
          </p>
          <p className="mt-2 text-sm text-yellow-600">
            O ciclo "{cycleId}" não existe ou foi removido.
          </p>
          <button
            onClick={() => navigate('/okr')}
            className="mt-4 rounded-lg bg-blue-600 px-6 py-2 font-montserrat font-medium text-white transition-colors hover:bg-blue-700"
          >
            Voltar para Ciclos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('space-y-6 p-6', className)}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-montserrat text-3xl font-bold text-gray-900">
            {cycle?.name}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {cycle?.description || 'Objetivos deste ciclo'}
          </p>
          {cycle && (
            <p className="mt-1 text-xs text-gray-500">
              {formatDateRangeIgnoringTimezone(cycle.startDate, cycle.endDate, {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            </p>
          )}
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-montserrat font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5" />
          Criar Objetivo
        </button>
      </div>

      {/* Objectives List */}
      {objectivesWithResponsibles.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {objectivesWithResponsibles.map((objective) => (
            <ObjectiveCard
              key={objective.id}
              objective={objective}
              onClick={handleObjectiveClick}
              showCycleBadge={false}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <p className="font-montserrat text-lg text-gray-500">
            Nenhum objetivo cadastrado neste ciclo.
          </p>
          <p className="mt-2 text-sm text-gray-400">
            Crie seu primeiro objetivo para este ciclo.
          </p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="mt-4 rounded-lg bg-blue-600 px-6 py-2 font-montserrat font-medium text-white transition-colors hover:bg-blue-700"
          >
            Criar Primeiro Objetivo
          </button>
        </div>
      )}

      {/* Create Modal */}
      <CreateObjectiveModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateObjective}
        defaultCycleId={cycleId}
      />
    </div>
  );
}
